import { adminService } from "@/api/admin/admin.service";
import type { TAdminUser, TReviewer } from "@/api/admin/admin.types";
import { UserPicker } from "@/components/admin";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
} from "@/components/data-grid";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { Loader2, UserMinus, UserPlus } from "lucide-react";
import { useState } from "react";

const PAGE_SIZE = 20;

export function ReviewersPage() {
  const { toast } = useToast();
  const [reloadTick, setReloadTick] = useState(0);

  const [selectedUser, setSelectedUser] = useState<TAdminUser | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  const [removeTarget, setRemoveTarget] = useState<TReviewer | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const reload = () => setReloadTick((t) => t + 1);

  const fetcher = async (query: TDataGridQuery) => {
    const { data, error: err } = await adminService.getReviewers({ signal: query.signal });
    if (err) throw err;
    return data;
  };

  const handleAssign = async () => {
    if (!selectedUser) {
      toast({ variant: "destructive", title: "Select a user first" });
      return;
    }
    setIsAssigning(true);
    const { error: err } = await adminService.assignReviewer(selectedUser.id);
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to assign reviewer",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "Reviewer role assigned" });
      setSelectedUser(null);
      reload();
    }
    setIsAssigning(false);
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setIsRemoving(true);
    const { error: err } = await adminService.removeReviewer(removeTarget.user_id);
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to remove reviewer",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "Reviewer role removed" });
      setRemoveTarget(null);
      reload();
    }
    setIsRemoving(false);
  };

  const fullName = (r: TReviewer) => `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim();

  const columns: TColumnDef<TReviewer>[] = [
    {
      id: "name",
      header: "Name",
      sortable: true,
      accessor: (r) => fullName(r).toLowerCase(),
      cell: (r) => <span className="font-medium text-foreground">{fullName(r) || "—"}</span>,
    },
    {
      id: "email",
      header: "Email",
      type: "email",
      sortable: true,
      accessor: (r) => r.email ?? "—",
    },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Reviewers</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage users assigned to the reviewer role
            </p>
          </div>
        </div>

        {/* Assign reviewer */}
        <div className="rounded-lg border border-border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold text-foreground">Assign reviewer role</h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="max-w-md flex-1">
              <UserPicker value={selectedUser} onChange={setSelectedUser} />
            </div>
            <Button onClick={() => void handleAssign()} disabled={isAssigning || !selectedUser}>
              {isAssigning ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Assigning…</>
              ) : (
                <><UserPlus className="mr-2 h-4 w-4" />Assign</>
              )}
            </Button>
          </div>
        </div>

        <DataGrid<TReviewer>
          getRowId={(row) => row.user_id}
          persistKey="admin.reviewers"
          ariaLabel="Reviewers"
          data={{
            mode: "client",
            fetcher,
            deps: [reloadTick],
            searchFields: ["first_name", "last_name", "email"],
          }}
          columns={columns}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search by name or email",
          }}
          rowActions={(r) => [
            {
              label: "Remove reviewer",
              icon: <UserMinus className="h-4 w-4" />,
              variant: "destructive",
              onClick: () => setRemoveTarget(r),
            },
          ]}
          emptyState={{ title: "No reviewers assigned yet." }}
        />
      </div>

      {/* Remove confirm */}
      <Dialog
        open={!!removeTarget}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Reviewer</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove the reviewer role from “
              {removeTarget ? fullName(removeTarget) || removeTarget.email : ""}”?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)} disabled={isRemoving}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void handleRemove()} disabled={isRemoving}>
              {isRemoving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Removing…</>
              ) : (
                "Remove"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
